// The chat API prefixes the errors it shows with `[chat_error:<code>]`,
// both in the stream's error text and in the JSON body of a rejected request
// (which the AI SDK transport surfaces as the raw body). See
// crm-chat-api/lib/chat-errors.ts. The CRM shows a translated message per
// code, and the technical detail under it.

export type ChatErrorCode =
  | 'heic_conversion_failed'
  | 'unsupported_image'
  | 'payload_too_large'
  | 'chat_failed';

const CODES: ReadonlySet<string> = new Set<ChatErrorCode>([
  'heic_conversion_failed',
  'unsupported_image',
  'payload_too_large',
  'chat_failed',
]);

const isChatErrorCode = (value: string): value is ChatErrorCode =>
  CODES.has(value);

const CODE_PREFIX = /\[chat_error:([a-z_]+)\]/;
const DETAIL = /\n\nDetails: ([\s\S]*)$/;

export type ParsedChatError = {
  code: ChatErrorCode | null;
  // The technical part worth showing small (empty when there is none).
  detail: string;
};

export const parseChatError = (message: string): ParsedChatError => {
  const code = CODE_PREFIX.exec(message)?.[1];
  if (code === undefined || !isChatErrorCode(code)) {
    return { code: null, detail: message };
  }
  return { code, detail: DETAIL.exec(message)?.[1]?.trim() ?? '' };
};

export const chatErrorMessage = (
  code: ChatErrorCode,
  t: (key: string) => string,
): string => {
  switch (code) {
    case 'heic_conversion_failed':
      return t(
        "This iPhone photo is in HEIC format and couldn't be converted. Share it as JPEG (Settings → Camera → Formats → Most Compatible) or take a screenshot of it.",
      );
    case 'unsupported_image':
      return t("One of the images couldn't be read. Try JPEG or PNG.");
    case 'payload_too_large':
      return t('The message is too large. Send fewer or smaller images.');
    case 'chat_failed':
      return t('Something went wrong while answering. Try again in a moment.');
  }
};
